import React, { useRef, useState, useCallback } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import {
  MAX_ATTACHMENTS_PER_TASK,
  MAX_ATTACHMENT_SIZE,
  ALLOWED_ATTACHMENT_TYPES,
  formatFileSize,
} from "../../lib/storage";

interface AttachmentUploaderProps {
  currentCount: number;
  onUpload: (file: File) => Promise<void>;
  disabled?: boolean;
  disabledMessage?: string;
}

const AttachmentUploader: React.FC<AttachmentUploaderProps> = ({
  currentCount,
  onUpload,
  disabled,
  disabledMessage,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remainingSlots = MAX_ATTACHMENTS_PER_TASK - currentCount;
  const canUpload = remainingSlots > 0 && !disabled;

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
      return `File type "${file.type || "unknown"}" is not supported`;
    }
    if (file.size > MAX_ATTACHMENT_SIZE) {
      return `File size exceeds ${formatFileSize(MAX_ATTACHMENT_SIZE)} limit`;
    }
    return null;
  };

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0 || !canUpload) return;

      setError(null);

      // Only process one file at a time
      const file = files[0];

      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      setIsUploading(true);
      try {
        await onUpload(file);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Upload failed");
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [canUpload, onUpload]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (canUpload) {
        setIsDragOver(true);
      }
    },
    [canUpload]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      if (canUpload) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [canUpload, handleFiles]
  );

  const handleClick = () => {
    if (canUpload && !isUploading) {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };

  const acceptTypes = ALLOWED_ATTACHMENT_TYPES.join(",");

  return (
    <div className="space-y-2">
      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          relative border-2 border-dashed rounded-lg px-4 py-3 text-center transition-all
          ${
            !canUpload
              ? "border-warm-border bg-warm-bg-elevated/50 cursor-not-allowed opacity-50"
              : isDragOver
                ? "border-warm-blue bg-warm-blue/10 cursor-pointer"
                : "border-warm-border hover:border-warm-text-tertiary hover:bg-warm-bg-elevated cursor-pointer"
          }
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          aria-label="Upload file"
          accept={acceptTypes}
          onChange={handleFileChange}
          disabled={!canUpload || isUploading}
          className="hidden"
        />

        {isUploading ? (
          <div className="flex items-center justify-center gap-2 text-warm-text-secondary">
            <Loader2 size={16} className="animate-spin" />
            <span className="text-sm">Uploading...</span>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 text-warm-text-secondary">
            <Upload size={16} />
            <span className="text-sm">
              {canUpload
                ? "Drop file or click to upload"
                : disabledMessage || `Maximum ${MAX_ATTACHMENTS_PER_TASK} attachments reached`}
            </span>
          </div>
        )}

      </div>

      {error && (
        <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg">
          <X size={14} className="text-red-400 flex-shrink-0" />
          <p className="text-xs text-red-400">{error}</p>
          <button
            onClick={() => setError(null)}
            aria-label="Dismiss error"
            className="ml-auto text-red-400 hover:text-red-300 transition-colors"
          >
            <X size={12} />
          </button>
        </div>
      )}
    </div>
  );
};

export default AttachmentUploader;
